/**
 * Fold an ordered, visibility-filtered RenderSegment list into render blocks:
 * consecutive tool calls collapse into one `tool-group` (rendered as a single
 * "Working / N steps" row), while sub-agent tools stay standalone `sub-agent`
 * blocks — they're structural, like desktop's Task lines, and never fold.
 *
 * Block ids reuse the first member's stable segment id, so React keeps group
 * identity (and expand state) as new calls stream into the group.
 */
import type { RenderSegment, ToolPart } from './message-parts';
import { FILE_WRITE_TOOLS, SUB_AGENT_TOOLS, toolNameOf } from './tools/tool-meta';

export type Block =
  | { kind: 'text'; id: string; text: string }
  | { kind: 'reasoning'; id: string; text: string }
  | { kind: 'tool-group'; id: string; parts: ToolPart[] }
  | { kind: 'sub-agent'; id: string; part: ToolPart }
  | { kind: 'file'; id: string; part: ToolPart };

/** The working middle of a turn, folded into one "worked" line (see foldTurn). */
export type TurnBlock = Block | { kind: 'worked'; id: string; blocks: Block[]; parts: ToolPart[] };

const isStep = (block: Block): boolean => block.kind !== 'text';

/** Every tool part inside a block list, in order. */
export function blockParts(blocks: Block[]): ToolPart[] {
  return blocks.flatMap((block) => {
    if (block.kind === 'tool-group') {
      return block.parts;
    }

    return block.kind === 'sub-agent' || block.kind === 'file' ? [block.part] : [];
  });
}

/**
 * Fold a turn the way Dunea Work does: everything from its first step to its
 * last — tool calls, thoughts, and the one-line narration between them — becomes
 * a single "worked" block. Text before the first step (the opening line) and
 * after the last (the answer / handoff) stays in view. A turn with no steps is
 * returned unchanged.
 */
export function foldTurn(blocks: Block[], live = false): TurnBlock[] {
  const first = blocks.findIndex(isStep);

  if (first === -1) {
    return blocks;
  }

  // While building, text after the last step is narration of the work in
  // progress, not the answer — the handoff only lands when the turn ends.
  let last = live ? blocks.length - 1 : first;

  for (let i = blocks.length - 1; i > first && !live; i--) {
    if (isStep(blocks[i])) {
      last = i;
      break;
    }
  }

  const inner = blocks.slice(first, last + 1);

  /*
   * Only the opening line stays above the fold. A saved turn keeps its text but
   * not its tool steps, so its narration lines arrive as paragraphs of the text
   * before the first remaining step — fold all but the first one in with it.
   */
  const opening: Block[] = [];
  const foldedLead: Block[] = [];

  for (const block of blocks.slice(0, first)) {
    if (block.kind === 'text' && opening.length === 0) {
      const [head, ...rest] = block.text.split(/\n{2,}/);

      opening.push({ ...block, text: head });

      if (rest.join('').trim()) {
        foldedLead.push({ kind: 'text', id: `${block.id}-lead`, text: rest.join('\n\n') });
      }
    } else if (opening.length === 0) {
      opening.push(block);
    } else {
      foldedLead.push(block);
    }
  }

  const folded = [...foldedLead, ...inner];

  return [
    ...opening,
    { kind: 'worked', id: `worked-${inner[0].id}`, blocks: folded, parts: blockParts(folded) },
    ...blocks.slice(last + 1),
  ];
}

export function foldSegments(segments: RenderSegment[]): Block[] {
  const blocks: Block[] = [];

  for (const seg of segments) {
    if (seg.kind === 'text' || seg.kind === 'reasoning') {
      blocks.push({ kind: seg.kind, id: seg.id, text: seg.text });
      continue;
    }

    if (SUB_AGENT_TOOLS.has(toolNameOf(seg.part))) {
      blocks.push({ kind: 'sub-agent', id: seg.id, part: seg.part });
      continue;
    }

    if (FILE_WRITE_TOOLS.has(toolNameOf(seg.part))) {
      blocks.push({ kind: 'file', id: seg.id, part: seg.part });
      continue;
    }

    const last = blocks[blocks.length - 1];

    if (last && last.kind === 'tool-group') {
      last.parts.push(seg.part);
    } else {
      blocks.push({ kind: 'tool-group', id: seg.id, parts: [seg.part] });
    }
  }

  return blocks;
}
